// Keep API and renderer copies in sync. Only allowlisted technical facts may be shared.
export const blueprintDiagnosticExtensions = [
  'adg',
  'adv',
  'fxp',
  'fxb',
  'vstpreset',
  'serumpreset',
  'vital',
  'phaseplant',
  'multipass',
  'snapheap',
  'fst',
  'nki',
  'nkm',
  'nksf',
  'nksfx',
  'h2p',
  'zip',
  '7z',
  'rar',
  'gz',
  'wav',
  'mp3',
  'flac',
  'png',
  'jpg',
  'mp4',
  'exe',
  'json',
  'xml',
  'none',
  'unknown',
] as const;
const rules = [
  'audio',
  'media',
  'active-content',
  'archive',
  'document',
  'size',
  'filename',
  'compressed-data',
  'nested-compression',
  'preset-container',
] as const;
const signatures = [
  'zip',
  'gzip',
  'rar',
  '7z',
  'bzip2',
  'xz',
  'zstd',
  'vst2',
  'vst3',
  'xfer-json',
  'riff',
  'xml',
  'json',
  'unrecognized',
  'not-inspected',
] as const;
const phases = [
  'metadata',
  'signature',
  'gzip-decode',
  'gzip-signature',
  'preset-container',
] as const;
const operations = ['prepare', 'publish', 'download'] as const;
const mimeFamilies = [
  'binary',
  'zip',
  'gzip',
  'json',
  'xml',
  'audio',
  'image',
  'video',
  'text',
  'other',
  'absent',
] as const;
export type BlueprintValidationDiagnostic = {
  schemaVersion: 1;
  validatorRevision: 1;
  operation: (typeof operations)[number];
  phase: (typeof phases)[number];
  rule: (typeof rules)[number];
  extension: (typeof blueprintDiagnosticExtensions)[number];
  extensionLength: number;
  mimeFamily: (typeof mimeFamilies)[number];
  sizeBytes: number;
  limitBytes: number;
  signature: (typeof signatures)[number];
  expandedBytes?: number;
  innerSignature?: (typeof signatures)[number];
  zipDirectoryFound?: boolean;
  zipEntryCount?: number;
};
const member = <T extends readonly string[]>(
  items: T,
  value: unknown
): value is T[number] =>
  typeof value === 'string' && (items as readonly string[]).includes(value);
const integer = (n: unknown): n is number =>
  typeof n === 'number' && Number.isSafeInteger(n) && n >= 0;
/** Reconstruct the payload instead of copying arbitrary server/error properties. */
export function sanitizeBlueprintValidation(
  value: unknown
): BlueprintValidationDiagnostic | undefined {
  if (!value || typeof value !== 'object') return;
  const v = value as Record<string, unknown>;
  if (
    v.schemaVersion !== 1 ||
    v.validatorRevision !== 1 ||
    !member(operations, v.operation) ||
    !member(phases, v.phase) ||
    !member(rules, v.rule) ||
    !member(blueprintDiagnosticExtensions, v.extension) ||
    !member(mimeFamilies, v.mimeFamily) ||
    !member(signatures, v.signature) ||
    !integer(v.extensionLength) ||
    v.extensionLength > 10000 ||
    !integer(v.sizeBytes) ||
    !integer(v.limitBytes)
  )
    return;
  return {
    schemaVersion: 1,
    validatorRevision: 1,
    operation: v.operation,
    phase: v.phase,
    rule: v.rule,
    extension: v.extension,
    extensionLength: v.extensionLength,
    mimeFamily: v.mimeFamily,
    sizeBytes: v.sizeBytes,
    limitBytes: v.limitBytes,
    signature: v.signature,
    ...(integer(v.expandedBytes) ? { expandedBytes: v.expandedBytes } : {}),
    ...(member(signatures, v.innerSignature)
      ? { innerSignature: v.innerSignature }
      : {}),
    ...(typeof v.zipDirectoryFound === 'boolean'
      ? { zipDirectoryFound: v.zipDirectoryFound }
      : {}),
    ...(integer(v.zipEntryCount) && v.zipEntryCount <= 65535
      ? { zipEntryCount: v.zipEntryCount }
      : {}),
  };
}
export function blueprintFileFacts(
  fileName: string,
  contentType: string,
  sizeBytes: number
) {
  const leaf = fileName.split(/[\\/]/).pop() || '';
  const suffix = leaf.includes('.')
    ? leaf.slice(leaf.lastIndexOf('.') + 1).toLowerCase()
    : '';
  const mime = contentType.split(';')[0].trim().toLowerCase();
  const mimeFamily: BlueprintValidationDiagnostic['mimeFamily'] = !mime
    ? 'absent'
    : mime === 'application/octet-stream'
      ? 'binary'
      : ['application/zip', 'application/x-zip-compressed'].includes(mime)
        ? 'zip'
        : ['application/gzip', 'application/x-gzip'].includes(mime)
          ? 'gzip'
          : mime === 'application/json'
            ? 'json'
            : ['application/xml', 'text/xml'].includes(mime)
              ? 'xml'
              : mime.startsWith('audio/')
                ? 'audio'
                : mime.startsWith('image/')
                  ? 'image'
                  : mime.startsWith('video/')
                    ? 'video'
                    : mime.startsWith('text/')
                      ? 'text'
                      : 'other';
  return {
    extension: member(blueprintDiagnosticExtensions, suffix)
      ? suffix
      : suffix
        ? ('unknown' as const)
        : ('none' as const),
    extensionLength: Math.min(suffix.length, 10000),
    mimeFamily,
    sizeBytes: integer(sizeBytes) ? sizeBytes : 0,
  };
}
/** Match fixed public signatures; never include a raw header or decoded user content. */
export function blueprintSignature(
  bytes: Uint8Array
): BlueprintValidationDiagnostic['signature'] {
  const at = (values: number[]) =>
    values.length <= bytes.length && values.every((v, i) => bytes[i] === v);
  if (at([80, 75, 3, 4]) || at([80, 75, 5, 6]) || at([80, 75, 7, 8]))
    return 'zip';
  if (at([31, 139])) return 'gzip';
  if (at([82, 97, 114, 33, 26, 7])) return 'rar';
  if (at([55, 122, 188, 175, 39, 28])) return '7z';
  if (at([66, 90, 104])) return 'bzip2';
  if (at([253, 55, 122, 88, 90, 0])) return 'xz';
  if (at([40, 181, 47, 253])) return 'zstd';
  if (at([67, 99, 110, 75])) return 'vst2';
  if (at([86, 83, 84, 51])) return 'vst3';
  if (at([88, 102, 101, 114, 74, 115, 111, 110])) return 'xfer-json';
  if (at([82, 73, 70, 70])) return 'riff';
  const text = new TextDecoder().decode(bytes.subarray(0, 128)).trimStart();
  return text.startsWith('<')
    ? 'xml'
    : text.startsWith('{')
      ? 'json'
      : 'unrecognized';
}
